import React, { lazy, Suspense, useState, useEffect } from "react"
import { components } from "react-select"
import PropTypes from "prop-types"
import styled, { css } from "styled-components"
import { useField } from "formik"
import { useIntl } from "gatsby-plugin-react-intl"

import {
  InputContainer,
  InputFieldLabel,
  InputFieldIcon,
  ErrorMessage,
  DisabledMessage,
} from "./common"

import { InputField } from "./Input"

import { media, mixins } from "../../styles"

import CheckmarkSVG from "../../images/icons/check--small.inline.svg"
import ChevronSVG from "../../images/icons/chevron.inline.svg"
import CrossSVG from "../../images/icons/cross--small.inline.svg"

const Select = lazy(() => import("react-select"))

const MultiInputField = styled(Select)`
  .Multiselect__control {
    background: transparent;
    border-radius: 0;
    box-shadow: none;
    font-size: inherit;
    padding: 5px 20px 3px 0;

    @media ${media.smDown} {
      min-height: 50px;
    }

    &,
    &:hover,
    &--is-focused {
      border: none;
      border-bottom: 1px solid ${({ theme }) => theme.sage};
      outline: none;
    }

    &--is-focused,
    &--is-focused:hover {
      border-color: ${({ theme }) => theme.sun};
    }
  }

  .Multiselect__value-container {
    overflow: visible;
    padding: 1px 0 0 0;

    > :last-child {
      margin: 0 2px;
    }
  }

  .Multiselect__menu {
    background: ${({ theme }) => theme.white};
    border: 0;
    border-radius: 0;
    box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.08);
    margin: 0;

    &-list {
      ${mixins.copy5};
      padding: 0;
    }
  }

  .Multiselect__dropdown-indicator,
  .Multiselect__indicator-separator,
  .Multiselect__clear-indicator {
    display: none;
  }

  .Multiselect__placeholder {
    line-height: 1.3;
  }

  .Multiselect__option {
    &:active,
    &--is-focused {
      background: ${({ theme }) => theme.greyWarm};
    }
  }

  .Multiselect__multi-value {
    background: ${({ theme }) => theme.greyWarm};
    border-radius: 0;
    margin: 2px 4px 2px 0;

    &__label {
      font-size: 13px;
      padding: 0 3px 0 6px;

      @media ${media.md} {
        font-size: 15px;
      }
    }

    &__remove {
      border-radius: 0;
      cursor: pointer;

      &:hover {
        background: ${({ theme }) => theme.rose};
        color: ${({ theme }) => theme.error};
      }
    }
  }

  ${(props) =>
    props.disabled &&
    css`
      cursor: default;
    `}

  ${(props) =>
    props.empty &&
    css`
      color: ${`rgba(${props.theme.spaceblueRGB}, 0.5)`};
    `}

  ${(props) =>
    props.$error &&
    css`
      .Multiselect__control {
        &,
        &:hover {
          border-bottom: 1px solid ${({ theme }) => theme.error};
        }
      }
    `}
`

// Remove aria-activedescendant warning
const Input = ({ children, ...props }) => (
  <components.Input {...props} aria-activedescendant={undefined} />
)

const MultiInput = ({
  className,
  disabled,
  forwardRef,
  hidden,
  hideLabel,
  hideRequired,
  isMulti = false,
  label,
  onBlur,
  onFocus,
  options,
  placeholder,
  required,
  ...props
}) => {
  const [field, meta, helpers] = useField(props)
  const { setValue } = helpers
  const [active, setActive] = useState(false)
  const [ok, setOk] = useState(false)
  const intl = useIntl()

  function onChange(option) {
    setValue(isMulti ? option.map((item) => item.value) : option.value)
  }

  const getValue = () => {
    if (options) {
      return isMulti
        ? options.filter((option) => field.value.indexOf(option.value) >= 0)
        : options.find((option) => option.value === field.value)
    } else {
      return isMulti ? [] : ""
    }
  }

  useEffect(() => {
    if (field.value || placeholder) {
      setActive(true)
    }

    if ((isMulti ? field.value.length === 0 : !field.value) || meta.error) {
      setOk(false)
    } else {
      setOk(true)
    }
  }, [field.value, meta.error, isMulti, placeholder])

  const handleFocus = (callback) => {
    setActive(true)
    setOk(false)
    callback && callback()
  }

  const handleBlur = (callback) => {
    if (!field.value && !placeholder) {
      setActive(false)
    }
    if ((isMulti ? field.value.length !== 0 : field.value) && !meta.error) {
      setOk(true)
    }
    callback && callback()
  }

  return (
    <InputContainer
      className={className}
      data-testid={`input-${field.name}`}
      hidden={hidden}
    >
      <InputFieldLabel
        $active={active}
        htmlFor={props.id || field.name}
        hidden={hideLabel}
        id={`aria-label-${props.id || field.name}`}
      >
        {label} {required && !hideRequired && "*"}
      </InputFieldLabel>
      <Suspense
        fallback={
          <InputField
            {...field}
            {...props}
            aria-labelledby={`aria-label-${props.id || field.name}`}
            disabled={disabled}
            data-fallback="true"
            $error={meta.touched && meta.error ? "true" : undefined}
            id={props.id || field.name}
            onBlur={() => handleBlur(onBlur)}
            onFocus={() => handleFocus(onFocus)}
            options={undefined}
            placeholder={
              placeholder &&
              `${placeholder}${required && hideLabel && !hideRequired ? " *" : ""}`
            }
            value={getValue()}
            ref={forwardRef}
          />
        }
      >
        <MultiInputField
          aria-labelledby={`aria-label-${props.id || field.name}`}
          blurInputOnSelect={!isMulti}
          classNamePrefix="Multiselect"
          closeMenuOnSelect={!isMulti}
          // defaultMenuIsOpen={true}
          delimiter=","
          components={{ Input }}
          $error={meta.touched && meta.error ? "true" : undefined}
          id={props.id || field.name}
          instanceId={field.name}
          isDisabled={disabled}
          isMulti={isMulti}
          name={field.name}
          noOptionsMessage={() => (
            <>{intl.formatMessage({ id: "forms.errors.noMoreOptions" })}</>
          )}
          onChange={onChange}
          onBlur={() => handleBlur(onBlur)}
          onFocus={() => handleFocus(onFocus)}
          options={options}
          openMenuOnFocus={true}
          onMenuOpen={handleFocus}
          placeholder={
            placeholder &&
            `${placeholder}${required && hideLabel && !hideRequired ? " *" : ""}`
          }
          value={getValue()}
          ref={forwardRef}
          tabSelectsValue={false}
        />
      </Suspense>
      {ok && (
        <InputFieldIcon data-testid="checkmark">
          <CheckmarkSVG />
        </InputFieldIcon>
      )}
      {meta.touched && meta.error ? (
        <>
          {meta.error && (
            <InputFieldIcon $error data-testid="errorcross">
              <CrossSVG />
            </InputFieldIcon>
          )}
          <ErrorMessage>
            {meta.error}
            {disabled && <DisabledMessage>{disabled}</DisabledMessage>}
          </ErrorMessage>
        </>
      ) : null}
      {!ok && !meta.error && (
        <InputFieldIcon
          $highlight
          disabled={disabled}
          style={{
            bottom: "14px",
            height: "20px",
            width: "20px",
          }}
        >
          <ChevronSVG />
        </InputFieldIcon>
      )}
      {disabled && !meta.error && <DisabledMessage>{disabled}</DisabledMessage>}
    </InputContainer>
  )
}

export default MultiInput

MultiInput.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  forwardRef: PropTypes.object,
  hidden: PropTypes.bool,
  hideLabel: PropTypes.bool,
  hideRequired: PropTypes.bool,
  id: PropTypes.string,
  isMulti: PropTypes.bool,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  options: PropTypes.array,
  placeholder: PropTypes.string.isRequired,
  required: PropTypes.bool,
}
